import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { Loader, ChevronLeft, User, Phone, Shield, AlertCircle, Package, CircleDollarSign, Clock, MessageCircle, Mail, Search } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface UserWithStats {
  id: string;
  phone: string;
  is_admin: boolean;
  created_at: string;
  stats: {
    total_orders: number;
    total_collectibles: number;
    last_active: string | null;
    recent_orders: {
      id: string;
      created_at: string;
      collectible_name: string;
      status: string;
    }[];
  };
}

function formatUserPhone(phone: string): string {
  // Check if it's an anonymous user
  if (phone.startsWith('anonymous-')) {
    // Extract last 4 characters of UUID
    const shortId = phone.slice(-4);
    return `Anonymous User #${shortId}`;
  }

  // Format phone number as +91 XXXXX XXXXX
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  }
  if (cleaned.length === 12 && cleaned.startsWith('91')) {
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
  }
  return phone;
}

function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<UserWithStats[]>([]);

  useEffect(() => {
    const checkAdminAndFetchUsers = async () => {
      try {
        // Check if user is admin
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('Not authenticated');
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .single();

        if (!profile?.is_admin) {
          throw new Error('Not authorized');
        }

        setIsAdmin(true);

        // Fetch users with their stats
        const { data: usersData, error: usersError } = await supabase
          .from('profiles')
          .select('*')
          .order('created_at', { ascending: false });

        if (usersError) throw usersError;

        // Fetch stats for each user
        const usersWithStats = await Promise.all((usersData || []).map(async (user) => {
          // Get order count
          const { count: orderCount } = await supabase
            .from('delivery_addresses')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id);

          // Get collectibles count
          const { count: collectiblesCount } = await supabase
            .from('collectibles')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id);

          // Get recent orders with collectible names
          const { data: recentOrders } = await supabase
            .from('delivery_addresses')
            .select(`
              id,
              created_at,
              status,
              collectible:collectibles (
                name
              )
            `)
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(3);

          // Get last active from most recent order or collectible
          const { data: lastActivity } = await supabase
            .from('delivery_addresses')
            .select('created_at')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          return {
            ...user,
            stats: {
              total_orders: orderCount || 0,
              total_collectibles: collectiblesCount || 0,
              last_active: lastActivity?.created_at || user.created_at,
              recent_orders: (recentOrders || []).map(order => ({
                id: order.id,
                created_at: order.created_at,
                collectible_name: order.collectible?.name || 'Unknown Item',
                status: order.status
              }))
            }
          };
        }));

        setUsers(usersWithStats);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    checkAdminAndFetchUsers();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = users.filter(user => 
      formatUserPhone(user.phone).toLowerCase().includes(query) ||
      user.id.toLowerCase().includes(query) ||
      (user.stats.recent_orders || []).some(order => 
        order.collectible_name.toLowerCase().includes(query)
      )
    );

    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to view this page.</p>
          <Link to="/" className="text-primary-600 hover:text-primary-800 font-medium">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Manage Users - Admin</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeft className="h-5 w-5" />
              Back to Home
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">Manage Users</h1>
          </div>
          <div className="bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-sm font-medium">
            {filteredUsers.length} Users
          </div>
        </div>

        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search users by phone, ID, or orders..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'No matching users found' : 'No Users Yet'}
            </h3>
            <p className="text-gray-600">
              {searchQuery 
                ? 'Try adjusting your search terms'
                : 'Users will appear here when they register or place orders.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {filteredUsers.map((user) => (
              <div
                key={user.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start gap-6">
                    <div className="h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="h-6 w-6 text-primary-600" />
                    </div>

                    <div className="flex-grow">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg font-medium text-gray-900">
                          {formatUserPhone(user.phone)}
                        </span>
                        {user.is_admin && (
                          <span className="bg-primary-50 text-primary-700 px-2 py-0.5 rounded text-sm font-medium flex items-center gap-1">
                            <Shield className="h-4 w-4" />
                            Admin
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span>ID: {user.id.slice(0, 8)}...</span>
                        <span>•</span>
                        <time>Joined {format(new Date(user.created_at), 'PPp')}</time>
                      </div>

                      <div className="grid grid-cols-3 gap-4 mt-6">
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center gap-2 text-gray-600 mb-2">
                            <Package className="h-4 w-4" />
                            <span className="text-sm font-medium">Total Orders</span>
                          </div>
                          <span className="text-2xl font-bold text-gray-900">
                            {user.stats.total_orders}
                          </span>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center gap-2 text-gray-600 mb-2">
                            <CircleDollarSign className="h-4 w-4" />
                            <span className="text-sm font-medium">Collectibles</span>
                          </div>
                          <span className="text-2xl font-bold text-gray-900">
                            {user.stats.total_collectibles}
                          </span>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center gap-2 text-gray-600 mb-2">
                            <Clock className="h-4 w-4" />
                            <span className="text-sm font-medium">Last Active</span>
                          </div>
                          <time className="text-sm font-medium text-gray-900">
                            {format(new Date(user.stats.last_active || user.created_at), 'PP')}
                          </time>
                        </div>
                      </div>

                      {user.stats.recent_orders.length > 0 && (
                        <div className="mt-6">
                          <h3 className="text-sm font-medium text-gray-900 mb-3">Recent Orders</h3>
                          <div className="space-y-3">
                            {user.stats.recent_orders.map(order => (
                              <div key={order.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                                <div>
                                  <p className="font-medium text-gray-900">{order.collectible_name}</p>
                                  <time className="text-sm text-gray-500">
                                    {format(new Date(order.created_at), 'PP')}
                                  </time>
                                </div>
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                  order.status === 'fulfilled'
                                    ? 'bg-green-50 text-green-700'
                                    : order.status === 'cancelled'
                                    ? 'bg-red-50 text-red-700'
                                    : 'bg-blue-50 text-blue-700'
                                }`}>
                                  {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mt-6 flex items-center gap-4">
                        {!user.phone.startsWith('anonymous-') && (
                          <>
                            <a
                              href={`tel:${user.phone}`}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors"
                            >
                              <Phone className="h-4 w-4" />
                              {formatUserPhone(user.phone)}
                            </a>
                            <a
                              href={`https://wa.me/${user.phone.replace(/\+/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                            >
                              <MessageCircle className="h-4 w-4" />
                              WhatsApp
                            </a>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default AdminUsers;